import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Plus, Check } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

/**
 * Pill-shaped property selector that lives in the header. Reads the
 * active account + property from AuthContext; opens a dropdown of
 * the user's properties in the active account, plus an "Add property"
 * link for admins. Switching invalidates the TanStack cache so the
 * new property doesn't inherit stale data.
 */
export default function PropertySwitcher() {
  const { account, properties, activeProperty, role, switchProperty } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function key(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  if (!account) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border border-rule bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary/40"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="property-switcher"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Property
        </span>
        <span className="truncate max-w-[140px]">
          {activeProperty?.name ?? 'None'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-md border border-rule bg-card shadow-lg"
        >
          <div className="px-3 py-2 border-b border-rule">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {account.name}
            </p>
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {properties.map((p) => {
              const active = p.id === activeProperty?.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      const wasDifferent = p.id !== activeProperty?.id;
                      switchProperty(p.id);
                      setOpen(false);
                      // Hard reload — same reason as AccountSwitcher.
                      // SPA navigate('/') is a no-op on the dashboard
                      // and leaves observed queries holding stale data
                      // from the previous property scope.
                      if (wasDifferent) window.location.assign('/');
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 px-3 py-2 text-sm text-left hover:bg-muted',
                      active && 'bg-accent/50',
                    )}
                    data-testid={`property-option-${p.slug}`}
                  >
                    <span className="truncate">{p.name}</span>
                    {active ? <Check className="h-4 w-4 text-primary shrink-0" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {role === 'admin' ? (
            <div className="border-t border-rule">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/settings?tab=properties');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-muted"
                data-testid="add-property"
              >
                <Plus className="h-4 w-4" aria-hidden /> Add property
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
